/**
 * Format JSON error bodies from our API (Zod ValidationError with `details`, or `{ error: string }`).
 */
export function formatApiErrorBody(body: unknown): string {
  if (body == null || typeof body !== 'object') {
    return 'Request failed';
  }
  const o = body as {
    error?: string;
    message?: string;
    details?: unknown;
  };

  const details = o.details;
  if (Array.isArray(details) && details.length > 0) {
    const lines = details.map((issue: unknown) =>
      typeof issue === 'string' ? issue : formatZodLikeIssue(issue)
    ).filter(Boolean);
    if (lines.length > 0) {
      const headline = typeof o.message === 'string' && o.message.trim() ? o.message.trim() : null;
      if (headline && headline !== 'Validation error') {
        return `${headline}\n${lines.join('\n')}`;
      }
      return lines.join('\n');
    }
  }

  if (typeof o.message === 'string' && o.message.trim() && o.message !== 'Validation error') {
    return o.message;
  }
  if (typeof o.error === 'string' && o.error.trim() && o.error !== 'ValidationError') return o.error;

  return 'Validation failed';
}

/** Read a failed fetch body without assuming JSON (proxies often return plain "Bad Gateway"). */
export async function messageForFailedResponse(
  res: Response,
  fallback: string,
): Promise<string> {
  const text = await res.text();
  const trimmed = text.trim();
  if (
    res.status === 502 ||
    res.status === 504 ||
    /^bad gateway$/i.test(trimmed) ||
    /gateway timeout/i.test(trimmed) ||
    /error occurred while trying to proxy/i.test(trimmed)
  ) {
    return 'The API server is not responding. Make sure it is running, then try again.';
  }
  try {
    return formatApiErrorBody(JSON.parse(trimmed));
  } catch {
    return trimmed.length > 0 && trimmed.length < 240 ? trimmed : fallback;
  }
}

function formatZodLikeIssue(issue: unknown): string {
  if (issue == null || typeof issue !== 'object') return String(issue);
  const i = issue as {
    path?: unknown;
    message?: string;
  };
  const msg = typeof i.message === 'string' ? i.message : '';
  const pathRaw = i.path;
  const path =
    Array.isArray(pathRaw) && pathRaw.length > 0
      ? pathRaw.map((p) => String(p)).join('.')
      : '';
  if (path && msg) return `${path}: ${msg}`;
  if (msg) return msg;
  return '';
}
